import {
	RiCalendar2Line,
	RiCoupon2Line,
	RiGroupLine,
	RiInformationLine,
} from '@remixicon/react';
import { differenceInHours, format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	choosePlatform,
	countRealTime,
	CurrencyFormatterNonDecimal,
	showWalletAndBalance,
	verifyDateTournament,
} from '../../../helpers/utils';
import { useNavigateStore } from '../../../store/navigation';
import { TournamentData } from '../../../types/types';
import { FutProIcon } from '../../Icons/FutProIcon';
import { Guaranteed } from '../../Icons/Guaranteed';
import { PickProIcon } from '../../Icons/PickProIcon';
import { TableEnrolls } from '../../TableEnrolls';
import Tooltip from '../../Tooltip';
import TooltipTournamentFeed from '../../Tooltip/TooltipTournamentFeed';
import TooltipTournamentInfo from '../../Tooltip/tournamentInfo';
import Button from '../../ui/button';
import Tags from '../../ui/tags';

interface IProps {
	data: TournamentData;
	navigationData: any;
}

export const TournamentWidgetView = ({ data, navigationData }: IProps) => {
	const navigate = useNavigate();
	const { saveNavigate } = useNavigateStore();
	const [remainingTime, setRemainingTime] = useState(
		countRealTime(data.entryEventAt)
	);
	const [showCounter, setShowCounter] = useState(false);

	useEffect(() => {
		if (data?.tournamentStatus === 0) {
			const now = new Date();
			const diffHours = differenceInHours(
				new Date(data.entryEventAt || ''),
				now
			);
			const closingSoon = diffHours < 4;
			setShowCounter(closingSoon);

			const interval = setInterval(() => {
				const time = countRealTime(data.entryEventAt);
				setRemainingTime(time);
				if (time.isFinished) {
					clearInterval(interval);
				}
			}, 1000);

			return () => clearInterval(interval);
		}
	}, []);

	return (
		<div className="w-full rounded-lg pb-3.5 bg-[#F5F5F5] shadow-md mb-2">
			<div className="w-full px-3 pt-4">
				<Tags status={data.tournamentStatus} />
			</div>

			<div className="flex w-full flex-row rounded-t-lg items-center justify-between px-3 pt-3 pb-4">
				<div className="flex flex-col justify-start items-start">
					<div className="text-custom-gray-11 text-base font-semibold font-bahnschrift">
						{data.leagueName}
					</div>
					<div className="text-custom-gray-11 text-sm font-light font-bahnschrift">
						{data.title}
					</div>
				</div>

				<div className="flex flex-col justify-end items-end">
					<div className="flex flex-col justify-end items-end gap-1">
						<div className="p-2 bg-white-100 rounded-lg flex-col justify-center items-center gap-2 inline-flex">
							<div className="flex-col justify-start items-start gap-1.5 flex">
								<div className="text-custom-gray-12 text-xs font-semibold font-bahnschrift">
									{data.tournamentStatus !== 0
										? format(
												data.entryEventAt,
												'dd/MM HH:mm'
										  )
										: verifyDateTournament(
												data.entryEventAt.replace(
													'Z',
													''
												)
										  )}
								</div>
							</div>
						</div>

						{showCounter && (
							<div className="flex flex-row gap-1 items-center justify-center bg-primary rounded-[80px] px-2 py-1">
								<span className="text-white text-xs font-semibold font-bahnschrift">
									Fecha em:
								</span>
								<span className="text-white text-xs font-semibold font-bahnschrift">
									{`${remainingTime.hours
										.toString()
										.padStart(2, '0')}h:` +
										`${remainingTime.minutes
											.toString()
											.padStart(2, '0')}m`}
								</span>
							</div>
						)}
					</div>
				</div>
			</div>

			<div className="flex w-full flex-row justify-between px-3.5 pb-3">
				<div className="grid grid-cols-3 justify-between w-full gap-2">
					<div className="flex flex-row justify-between">
						<div className="flex flex-col justify-start items-start">
							<Tooltip
								icon={
									<>
										<div className="text-left flex w-full justify-center items-center gap-1 text-custom-gray-11 text-sm font-light font-bahnschrift pb-1">
											Premiação
											<RiInformationLine
												color="#636363"
												className="w-4 h-4 mt-0.5"
											/>
										</div>
										<div>
											{Number(data.prizeAmt) === 0 &&
											Number(data.prizeAmtBonus) === 0 ? (
												<div className="text-custom-gray-11 text-xs leading-3 font-bahnschrift font-semibold uppercase">
													Sem Premiação
												</div>
											) : (
												<>
													{Number(data.prizeAmt) !==
													0 ? (
														<span className="text-custom-gray-11 text-lg leading-3 font-semibold font-bahnschrift flex justify-end items-center gap-1">
															{showWalletAndBalance() ? (
																'R$ '
															) : choosePlatform() ? (
																<PickProIcon
																	style="w-3.5 h-3.5 mr-1"
																	color="#636363"
																/>
															) : (
																<FutProIcon
																	style="w-3.5 h-3.5 mr-1"
																	color="#636363"
																/>
															)}
															{CurrencyFormatterNonDecimal(
																Number(
																	data.prizeAmt
																)
															)}
														</span>
													) : (
														Number(
															data.prizeAmtBonus
														) !== 0 && (
															<span className="text-custom-gray-11 text-lg leading-3 font-semibold font-bahnschrift flex justify-end items-center gap-1">
																{'B$ '}
																{CurrencyFormatterNonDecimal(
																	Number(
																		data.prizeAmtBonus
																	)
																)}
															</span>
														)
													)}
													{Number(
														data.prizeAmtBonus
													) !== 0 &&
														Number(
															data.prizeAmt
														) !== 0 && (
															<span className="text-custom-gray-11 text-xs font-semibold font-bahnschrift flex justify-end items-end gap-1">
																+ {'B$ '}
																{CurrencyFormatterNonDecimal(
																	Number(
																		data.prizeAmtBonus
																	)
																)}
															</span>
														)}
												</>
											)}
										</div>
									</>
								}
								infoIcon="warning"
								title="Premiação"
								message={
									<>
										{Number(data.prizeAmt) === 0 &&
											Number(data.prizeAmtBonus) ===
												0 && (
												<p className="text-[#535861] text-sm font-light font-bahnschrift leading-tight">
													Este torneio não distribui
													prêmios.
												</p>
											)}
										{Number(data.prizeAmt) !== 0 &&
											Number(data.prizeAmtBonus) !==
												0 && (
												<p className="text-[#535861] text-sm font-light font-bahnschrift leading-tight">
													Este torneio oferece R${' '}
													{CurrencyFormatterNonDecimal(
														Number(data.prizeAmt)
													)}
													, que serão distribuídos
													entre os ganhadores, além de
													B${' '}
													{CurrencyFormatterNonDecimal(
														Number(
															data.prizeAmtBonus
														)
													)}{' '}
													em bônus.
												</p>
											)}

										{Number(data.prizeAmt) !== 0 &&
											Number(data.prizeAmtBonus) ===
												0 && (
												<p className="text-[#535861] text-sm font-light font-bahnschrift leading-tight">
													Este torneio oferece R$
													{CurrencyFormatterNonDecimal(
														Number(data.prizeAmt)
													)}
													, que serão distribuídos
													entre os ganhadores.
												</p>
											)}
										{Number(data.prizeAmt) === 0 &&
											Number(data.prizeAmtBonus) !==
												0 && (
												<p className="text-[#535861] text-sm font-light font-bahnschrift leading-tight">
													Este torneio oferece B${' '}
													{CurrencyFormatterNonDecimal(
														Number(
															data.prizeAmtBonus
														)
													)}
													, em bônus que serão
													distribuídos entre os
													ganhadores
												</p>
											)}
									</>
								}
							/>
						</div>
					</div>
					<div className="flex flex-row justify-center">
						<div className="flex flex-col justify-start items-start">
							<div className="text-custom-gray-11 text-sm font-light font-bahnschrift ">
								Entrada
							</div>

							{Number(data.entryAmt) === 0 ? (
								<div className="text-custom-gray-11 text-sm animate-blink leading-3 font-bahnschrift font-semibold uppercase">
									Gratuito
								</div>
							) : (
								<span className="text-custom-gray-11 text-base leading-4 font-semibold font-bahnschrift flex justify-center items-center gap-1">
									{showWalletAndBalance() ? (
										'R$ '
									) : choosePlatform() ? (
										<PickProIcon
											style="w-3.5 h-3.5 mr-1"
											color="#636363"
										/>
									) : (
										<FutProIcon
											style="w-3.5 h-3.5 mr-1"
											color="#636363"
										/>
									)}
									{CurrencyFormatterNonDecimal(
										Number(data.entryAmt)
									)}
								</span>
							)}
						</div>
					</div>

					<div className="flex flex-col justify-between gap-1">
						<TooltipTournamentInfo
							title="Informações"
							infoIcon="warning"
							tournament={data}
							children={
								<>
									<RiGroupLine
										color="#636363"
										size={24}
										className="mt-1"
									/>
									<span className="text-primary text-2xl font-semibold font-bahnschrift">
										{data.qtdRegistration}
									</span>
									<div className="flex flex-col justify-start items-start">
										<div className="text-custom-gray-11 text-sm flex w-full items-center justify-between leading-3 relative font-semibold font-bahnschrift">
											{Number(data.entryMinQtd) === 0 ? (
												<Guaranteed
													color="#979797"
													className="w-[18px] h-[18px]"
												/>
											) : (
												data.entryMinQtd
											)}
											<RiInformationLine
												color="#636363"
												className="w-4 h-4"
											/>
										</div>
										{Number(data.entryMaxQtd) !== 0 && (
											<span className="text-custom-gray-11 text-sm leading-3 font-semibold font-bahnschrift">
												{data.entryMaxQtd}
											</span>
										)}
									</div>
								</>
							}
						/>
					</div>
				</div>
			</div>

			<div className="px-3.5">
				<Button
					className="py-2.5 w-full"
					textCustom="text-base font-normal"
					onClick={() => {
						navigate(`/tournaments/${data.id}`);
						saveNavigate(navigationData);
					}}
				>
					{data?.tournamentStatus !== 0 ? 'Consultar' : 'Participar'}
				</Button>
			</div>

			<div className="flex w-full flex-row justify-between px-3.5 pt-3">
				<div className="flex flex-col justify-between gap-2">
					{(!data.multipleEntry || data.singleMatch) && (
						<div className="flex flex-row justify-between">
							<div className="flex justify-start items-center gap-2">
								{data.singleMatch && (
									<Tooltip
										icon={
											<div className="flex flex-row items-center justify-center gap-1">
												<RiCalendar2Line
													size={16}
													color="#636363"
												/>
												<span className="text-custom-gray-11 text-xs font-light font-bahnschrift">
													Jogo único
												</span>
											</div>
										}
										infoIcon="warning"
										title="Único Jogo"
										message="Este torneio conta com um único jogo."
									/>
								)}
								{!data.multipleEntry && (
									<Tooltip
										icon={
											<div className="flex flex-row items-center justify-center gap-1">
												<RiCoupon2Line
													size={16}
													color="#636363"
												/>
												<span className="text-custom-gray-11 text-xs font-light font-bahnschrift">
													Entrada única
												</span>
											</div>
										}
										title="Entrada única"
										infoIcon="warning"
										message="É permitida apenas uma inscrição por usuário neste torneio."
									/>
								)}
							</div>
						</div>
					)}
					{data.teamsByEvents && data.teamsByEvents.length > 0 && (
						<div className="self-stretch justify-start items-end gap-2.5 inline-flex">
							<div
								className={
									'grid max-[375px]:grid-cols-2 grid-cols-4 lg:grid-cols-6' +
									(data.modality?.name === 'Basquete'
										? ' gap-2'
										: '')
								}
							>
								{data.teamsByEvents.map(
									(team: any, index: number) => (
										<div
											key={index}
											className={
												'flex flex-row items-center font-bahnschrift text-neutral-400 text-xs' +
												(data.modality?.name ===
												'Basquete'
													? ' gap-0.5'
													: '')
											}
										>
											<img
												className="w-6 h-6 object-scale-down"
												src={team.teamHome.image}
												alt={team.teamHome.name}
											/>
											x
											<img
												className="w-6 h-6 object-scale-down"
												src={team.teamAway.image}
												alt={team.teamAway.name}
											/>
										</div>
									)
								)}
							</div>
						</div>
					)}
				</div>
				<div className="flex flex-row">
					{data.experienceBadget.length === 1 &&
						data.experienceBadget.map((exp) => (
							<TooltipTournamentFeed experienceBadget={exp} />
						))}
				</div>
			</div>

			{data.participants.filter((enroll: any) => enroll.reqIsPlayer)
				.length > 0 && (
				<div className="px-3.5 pt-2">
					<TableEnrolls
						thead={[
							{
								title:
									data.participants.filter(
										(enroll: any) => enroll.reqIsPlayer
									).length === 1
										? 'Minha Inscrição'
										: 'Minhas Inscrições',
							},
							{
								title: 'Posição',
							},
							{
								title: 'Pontos',
							},
							{
								title: 'Prêmio',
							},
						]}
						tbody={data.participants.filter(
							(enroll: any) => enroll.reqIsPlayer
						)}
						navigation={navigationData}
					/>
				</div>
			)}
		</div>
	);
};
